import React, { useEffect, useMemo, useState } from "react";

import {
  Settings,
  Save,
  RotateCcw,
  History,
  Info,
  TrendingUp,
  MapPin,
  Award,
  Zap,
  Activity,
  Briefcase,
  DollarSign,
  AlertTriangle,
  X,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";

import Button from "../../../Components/Button";
import CustomCheckbox from "../../../Components/CheckCircle";
import CommonInput from "../../../Components/Input";
import { useAppDispatch, useAppSelector } from "../../../Store/Hooks";
import {
  clearAssignmentRecommendationUpdateState,
  fetchAssignmentRecommendationHistory,
  fetchAssignmentRecommendationSettings,
  updateAssignmentRecommendationSettings,
} from "../../../Store/Reducers/VendorAdminSlice";
import { toast } from "../../../Utils/Helper/toast";

type FactorConfig = {
  icon: LucideIcon;
  label: string;
  description: string;
  color: string;
  bgColor: string;
  available: boolean;
  comingSoon?: boolean;
};

const factorConfig = {
  utilization: {
    icon: TrendingUp,
    label: "Utilization",
    description: "Executive capacity availability (current accounts vs limit)",
    color: "text-blue-600",
    bgColor: "bg-blue-50",
    available: true,
  },
  geographic: {
    icon: MapPin,
    label: "Geographic Match",
    description: "Country and region alignment between executive and account",
    color: "text-green-600",
    bgColor: "bg-green-50",
    available: true,
  },
  grade_match: {
    icon: Award,
    label: "Grade Match",
    description: "Executive grade vs account tier/size alignment",
    color: "text-purple-600",
    bgColor: "bg-purple-50",
    available: true,
  },
  performance: {
    icon: Zap,
    label: "Performance Score",
    description: "Executive performance rating and track record",
    color: "text-yellow-600",
    bgColor: "bg-yellow-50",
    available: false,
    comingSoon: true,
  },
  availability: {
    icon: Activity,
    label: "Availability Status",
    description: "Current availability status of executive",
    color: "text-indigo-600",
    bgColor: "bg-indigo-50",
    available: false,
    comingSoon: true,
  },
  industry: {
    icon: Briefcase,
    label: "Industry Experience",
    description: "Experience in account industry (optional)",
    color: "text-orange-600",
    bgColor: "bg-orange-50",
    available: false,
    comingSoon: true,
  },
  account_size: {
    icon: DollarSign,
    label: "Account Size Match",
    description: "Account revenue/value vs executive experience (optional)",
    color: "text-emerald-600",
    bgColor: "bg-emerald-50",
    available: false,
    comingSoon: true,
  },
  churn_risk: {
    icon: AlertTriangle,
    label: "Churn Risk Handling",
    description: "Capability to handle high-risk accounts (optional)",
    color: "text-red-600",
    bgColor: "bg-red-50",
    available: false,
    comingSoon: true,
  },
} satisfies Record<string, FactorConfig>;

type FactorKey = keyof typeof factorConfig;
const factorKeys = Object.keys(factorConfig) as FactorKey[];
const factorEntries = Object.entries(factorConfig) as [
  FactorKey,
  FactorConfig,
][];

type WeightKey = `${FactorKey}_weight`;
type EnabledKey = `${FactorKey}_enabled`;
type PriorityKey = `${FactorKey}_priority`;

const getWeightKey = (factor: FactorKey) => `${factor}_weight` as WeightKey;
const getEnabledKey = (factor: FactorKey) => `${factor}_enabled` as EnabledKey;
const getPriorityKey = (factor: FactorKey) =>
  `${factor}_priority` as PriorityKey;

type RecommendationSettings = {
  utilization_weight: number;
  geographic_weight: number;
  grade_match_weight: number;
  performance_weight: number;
  availability_weight: number;
  industry_weight: number;
  account_size_weight: number;
  churn_risk_weight: number;
  utilization_enabled: boolean;
  geographic_enabled: boolean;
  grade_match_enabled: boolean;
  performance_enabled: boolean;
  availability_enabled: boolean;
  industry_enabled: boolean;
  account_size_enabled: boolean;
  churn_risk_enabled: boolean;
  utilization_priority: number;
  geographic_priority: number;
  grade_match_priority: number;
  performance_priority: number;
  availability_priority: number;
  industry_priority: number;
  account_size_priority: number;
  churn_risk_priority: number;
};

const DEFAULT_SETTINGS: RecommendationSettings = {
  utilization_weight: 55,
  geographic_weight: 25,
  grade_match_weight: 20,
  performance_weight: 0,
  availability_weight: 0,
  industry_weight: 0,
  account_size_weight: 0,
  churn_risk_weight: 0,
  utilization_enabled: true,
  geographic_enabled: true,
  grade_match_enabled: true,
  performance_enabled: false,
  availability_enabled: false,
  industry_enabled: false,
  account_size_enabled: false,
  churn_risk_enabled: false,
  utilization_priority: 1,
  geographic_priority: 2,
  grade_match_priority: 3,
  performance_priority: 4,
  availability_priority: 5,
  industry_priority: 6,
  account_size_priority: 7,
  churn_risk_priority: 8,
};

const SETTINGS_KEYS = Object.keys(DEFAULT_SETTINGS) as Array<
  keyof RecommendationSettings
>;

const cloneSettings = (settings: RecommendationSettings) => ({ ...settings });

type SettingsValue = RecommendationSettings[keyof RecommendationSettings];

const normalizeSettings = (data: any): RecommendationSettings => {
  const source = data?.settings || data || {};
  const normalized: RecommendationSettings = { ...DEFAULT_SETTINGS };
  SETTINGS_KEYS.forEach(key => {
    if (source[key] !== undefined && source[key] !== null) {
      (normalized as Record<string, SettingsValue>)[key] = source[
        key
      ] as SettingsValue;
    }
  });
  return normalized;
};

const MAX_REASON_LENGTH = 150;

const AssignmentRecommendationSettings: React.FC = () => {
  const dispatch = useAppDispatch();
  const {
    assignmentRecommendationSettings,
    assignmentRecommendationSettingsLoading,
    assignmentRecommendationSettingsError,
    assignmentRecommendationHistory,
    assignmentRecommendationHistoryLoading,
    assignmentRecommendationHistoryError,
    assignmentRecommendationUpdateLoading,
    assignmentRecommendationUpdateError,
    assignmentRecommendationUpdateSuccess,
  } = useAppSelector((state: any) => state.vendorAdmin);

  const [settings, setSettings] = useState<RecommendationSettings | null>(null);
  const [originalSettings, setOriginalSettings] =
    useState<RecommendationSettings | null>(null);
  const [isDefault, setIsDefault] = useState(false);
  const [changeReason, setChangeReason] = useState("");
  const [showHistory, setShowHistory] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);

  useEffect(() => {
    dispatch(fetchAssignmentRecommendationSettings());
  }, [dispatch]);

  useEffect(() => {
    if (assignmentRecommendationSettings) {
      const normalized = normalizeSettings(assignmentRecommendationSettings);
      setSettings(normalized);
      setOriginalSettings(cloneSettings(normalized));
      setIsDefault(
        Boolean(
          assignmentRecommendationSettings?.is_default ??
            assignmentRecommendationSettings?.isDefault
        )
      );
    } else if (!assignmentRecommendationSettingsLoading) {
      const defaults = cloneSettings(DEFAULT_SETTINGS);
      setSettings(defaults);
      setOriginalSettings(cloneSettings(defaults));
      setIsDefault(true);
    }
  }, [
    assignmentRecommendationSettings,
    assignmentRecommendationSettingsLoading,
  ]);

  const hasChanges = useMemo(() => {
    if (!settings || !originalSettings) return false;
    return SETTINGS_KEYS.some(key => settings[key] !== originalSettings[key]);
  }, [settings, originalSettings]);

  useEffect(() => {
    if (assignmentRecommendationSettingsError) {
      toast.error(assignmentRecommendationSettingsError);
    }
  }, [assignmentRecommendationSettingsError]);

  useEffect(() => {
    if (assignmentRecommendationHistoryError) {
      toast.error(assignmentRecommendationHistoryError);
    }
  }, [assignmentRecommendationHistoryError]);

  useEffect(() => {
    if (assignmentRecommendationUpdateSuccess) {
      toast.success("Recommendation settings saved successfully!");
      setChangeReason("");
      setShowConfirmation(false);
      setIsDefault(false);
      dispatch(fetchAssignmentRecommendationSettings());
      dispatch(clearAssignmentRecommendationUpdateState());
    }
  }, [assignmentRecommendationUpdateSuccess, dispatch]);

  useEffect(() => {
    if (assignmentRecommendationUpdateError) {
      toast.error(assignmentRecommendationUpdateError);
      dispatch(clearAssignmentRecommendationUpdateState());
    }
  }, [assignmentRecommendationUpdateError, dispatch]);

  const handleWeightChange = (factor: FactorKey, value: string) => {
    const numValue = value === "" ? 0 : parseInt(value, 10);
    const newValue = Math.max(0, Math.min(100, numValue || 0));
    setSettings(prev =>
      prev
        ? {
            ...prev,
            [getWeightKey(factor)]: newValue,
          }
        : prev
    );
  };

  const handlePriorityChange = (factor: FactorKey, value: string) => {
    const numValue = value === "" ? 1 : parseInt(value, 10);
    const newValue = Math.max(1, Math.min(10, numValue || 1));
    setSettings(prev =>
      prev
        ? {
            ...prev,
            [getPriorityKey(factor)]: newValue,
          }
        : prev
    );
  };

  const handleToggle = (factor: FactorKey) => {
    setSettings(prev => {
      if (!prev) return prev;
      const newEnabled = !prev[getEnabledKey(factor)];
      return {
        ...prev,
        [getEnabledKey(factor)]: newEnabled,
        [getWeightKey(factor)]: newEnabled ? prev[getWeightKey(factor)] : 0,
      } as RecommendationSettings;
    });
  };

  const totalWeight = useMemo(() => {
    if (!settings) return 0;
    return factorKeys.reduce((sum, factor) => {
      return sum + settings[getWeightKey(factor)];
    }, 0);
  }, [settings]);

  const allEnabled = useMemo(() => {
    if (!settings) return false;
    return factorKeys.every(factor => settings[getEnabledKey(factor)]);
  }, [settings]);

  const handleSaveClick = () => {
    if (totalWeight !== 100) {
      toast.error(`Weights must sum to 100%. Current sum: ${totalWeight}%`);
      return;
    }

    if (!hasChanges) {
      toast.info("No changes to save");
      return;
    }

    if (!changeReason.trim()) {
      toast.error("Please provide a reason for this change");
      return;
    }

    if (changeReason.length > MAX_REASON_LENGTH) {
      toast.error(`Reason must be ${MAX_REASON_LENGTH} characters or less`);
      return;
    }

    setShowConfirmation(true);
  };

  const handleConfirmSave = async () => {
    if (!settings) return;
    await dispatch(
      updateAssignmentRecommendationSettings({
        settings,
        changeReason,
      })
    );
  };

  const handleReset = () => {
    if (!originalSettings) return;
    setSettings(cloneSettings(originalSettings));
    setChangeReason("");
    toast.info("Reset to current saved settings");
  };

  const handleOpenHistory = () => {
    dispatch(fetchAssignmentRecommendationHistory());
    setShowHistory(true);
  };

  if (assignmentRecommendationSettingsLoading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
        </div>
      </div>
    );
  }

  if (!settings) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="text-center py-12">
          <p className="text-gray-600">
            Failed to load settings. Please refresh the page.
          </p>
        </div>
      </div>
    );
  }

  const isValid = totalWeight === 100;
  const history = assignmentRecommendationHistory || [];

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
      <div className="p-6 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center space-x-3">
              <Settings className="h-5 w-5 text-purple-600" />
              <h2 className="text-xl font-bold text-gray-900">
                Assignment Recommendation Settings
              </h2>
              {isDefault && (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                  <Info className="h-3 w-3 mr-1" />
                  Default
                </span>
              )}
              <span
                className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                  isValid
                    ? "bg-green-100 text-green-800"
                    : "bg-red-100 text-red-800"
                }`}
              >
                Total: {totalWeight}%
              </span>
            </div>
            <p className="text-sm text-gray-600 mt-1">
              Configure recommendation factors (weights must sum to 100%)
            </p>
          </div>
          <Button
            variant="outline-primary"
            onClick={handleOpenHistory}
            icon={<History className="h-3 w-3" />}
            className="text-xs"
          >
            History
          </Button>
        </div>
      </div>

      <div className="p-6">
        <div className="mb-6 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="space-y-2 text-xs">
            <div className="flex items-start space-x-2">
              <Info className="h-4 w-4 text-blue-600 flex-shrink-0" />
              <div>
                <span className="font-semibold text-blue-900 ">Weight:</span>
                <span className="text-blue-800 ml-1">
                  Higher % = Higher importance. Must sum to 100%.
                </span>
              </div>
            </div>
            <div className="flex items-start space-x-2">
              <Info className="h-4 w-4 text-blue-600 flex-shrink-0" />
              <div>
                <span className="font-semibold text-blue-900 ">Priority:</span>
                <span className="text-blue-800 ml-1">
                  Range 1-10. <strong>1 = Top priority</strong>,{" "}
                  <strong>10 = Least priority</strong>.
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left text-xs font-semibold text-gray-700 pb-3 w-12">
                  <CustomCheckbox
                    checked={allEnabled}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      const newEnabled = e.target.checked;
                      setSettings(prev => {
                        if (!prev) return prev;
                        const updated = { ...prev } as RecommendationSettings;
                        factorKeys.forEach(factor => {
                          updated[getEnabledKey(factor)] = newEnabled;
                        });
                        return updated;
                      });
                    }}
                  />
                </th>
                <th className="text-left text-xs font-semibold text-gray-700 pb-3">
                  Factor
                </th>
                <th className="text-left text-xs font-semibold text-gray-700 pb-3 w-32">
                  Weight
                  <span className="text-gray-500 font-normal ml-1">
                    (0-100%)
                  </span>
                </th>
                <th className="text-left text-xs font-semibold text-gray-700 pb-3 w-32">
                  Priority
                  <span className="text-gray-500 font-normal ml-1">(1-10)</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {factorEntries.map(([factor, config]) => {
                const IconComponent = config.icon;
                const weight = settings[getWeightKey(factor)] || 0;
                const enabled = settings[getEnabledKey(factor)] !== false;
                const priority = settings[getPriorityKey(factor)] || 1;

                return (
                  <tr
                    key={factor}
                    className={`border-b border-gray-100 ${
                      !enabled ? "opacity-50" : ""
                    }`}
                  >
                    <td className="py-3">
                      <div
                        className={
                          config.comingSoon
                            ? "cursor-not-allowed opacity-50"
                            : ""
                        }
                        title={
                          config.comingSoon
                            ? "Coming Soon - Not available yet"
                            : ""
                        }
                      >
                        <CustomCheckbox
                          checked={enabled}
                          onChange={() => handleToggle(factor)}
                          disabled={config.comingSoon}
                        />
                      </div>
                    </td>

                    <td className="py-3">
                      <div className="flex items-center space-x-3">
                        <div
                          className={`flex-shrink-0 p-2 rounded ${config.bgColor}`}
                        >
                          <IconComponent
                            className={`h-4 w-4 ${config.color}`}
                          />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center space-x-2">
                            <div className="text-sm font-medium text-gray-900">
                              {config.label}
                            </div>
                            {config.comingSoon && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 border border-blue-200">
                                Coming Soon
                              </span>
                            )}
                          </div>
                          <div className="text-xs text-gray-500">
                            {config.description}
                          </div>
                        </div>
                      </div>
                    </td>

                    <td className="py-3">
                      {enabled ? (
                        <div className="flex items-center space-x-2">
                          <CommonInput
                            variant="field"
                            name={`${factor}-weight`}
                            type="number"
                            min="0"
                            max="100"
                            value={weight.toString()}
                            onChange={e =>
                              handleWeightChange(factor, e.target.value)
                            }
                            className="w-20 text-sm"
                          />
                          <span className="text-sm text-gray-600">%</span>
                        </div>
                      ) : (
                        <span className="text-sm text-gray-400">-</span>
                      )}
                    </td>

                    <td className="py-3">
                      {enabled ? (
                        <CommonInput
                          variant="field"
                          name={`${factor}-priority`}
                          type="number"
                          min="1"
                          max="10"
                          value={priority.toString()}
                          onChange={e =>
                            handlePriorityChange(factor, e.target.value)
                          }
                          className="w-20 text-sm"
                        />
                      ) : (
                        <span className="text-sm text-gray-400">-</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {hasChanges && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="flex items-end space-x-3">
              <div className="flex-1">
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-xs font-medium text-gray-700">
                    Reason for Change *
                  </label>
                  <span
                    className={`text-xs ${
                      changeReason.length > MAX_REASON_LENGTH
                        ? "text-red-600 font-medium"
                        : changeReason.length > MAX_REASON_LENGTH * 0.8
                          ? "text-yellow-600"
                          : "text-gray-500"
                    }`}
                  >
                    {changeReason.length}/{MAX_REASON_LENGTH}
                  </span>
                </div>
                <CommonInput
                  variant="field"
                  name="assignment-recommendation-change-reason"
                  value={changeReason}
                  onChange={e =>
                    setChangeReason(e.target.value.slice(0, MAX_REASON_LENGTH))
                  }
                  placeholder="e.g., Prioritizing geographic match for Q1 2024"
                  maxLength={MAX_REASON_LENGTH}
                  className="text-sm"
                />
              </div>
              <Button
                variant="gray"
                onClick={handleReset}
                disabled={assignmentRecommendationUpdateLoading}
                icon={<RotateCcw className="h-3 w-3" />}
                className="text-sm"
              >
                Reset
              </Button>
              <Button
                variant="primary"
                onClick={handleSaveClick}
                disabled={
                  !isValid ||
                  assignmentRecommendationUpdateLoading ||
                  !changeReason.trim() ||
                  !hasChanges
                }
                icon={<Save className="h-3 w-3" />}
                loading={assignmentRecommendationUpdateLoading}
                className="text-sm"
              >
                Save Settings
              </Button>
            </div>
          </div>
        )}
      </div>

      {showConfirmation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full">
            <div className="p-6">
              <h3 className="text-lg font-bold text-gray-900  mb-4">
                Confirm Settings Update
              </h3>

              <div className="mb-6 space-y-4">
                <div className="bg-blue-50  p-4 rounded-lg">
                  <h4 className="text-sm font-semibold text-blue-900  mb-2">
                    Summary of Changes
                  </h4>
                  <div className="space-y-2 text-sm">
                    {factorEntries.map(([factor, config]) => {
                      const enabled = settings[getEnabledKey(factor)];
                      const weight = settings[getWeightKey(factor)];
                      const priority = settings[getPriorityKey(factor)];

                      if (enabled) {
                        return (
                          <div
                            key={factor}
                            className="flex items-center justify-between text-blue-800"
                          >
                            <span className="font-medium">{config.label}</span>
                            <span>
                              Weight: {weight}% | Priority: {priority}
                            </span>
                          </div>
                        );
                      }
                      return null;
                    })}
                  </div>
                  <div className="mt-3 pt-3 border-t border-blue-200">
                    <div className="flex items-center justify-between font-semibold text-blue-900 ">
                      <span>Total Weight:</span>
                      <span>{totalWeight}%</span>
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Reason for Change:
                  </label>
                  <p className="text-sm text-gray-900  bg-gray-100 p-3 rounded">
                    {changeReason}
                  </p>
                </div>

                <div className="bg-yellow-50 border border-yellow-200 p-3 rounded">
                  <p className="text-xs text-yellow-800">
                    ⚠️ This will affect how executives are recommended for
                    account assignments across your organization.
                  </p>
                </div>
              </div>

              <div className="flex items-center justify-end space-x-3">
                <Button
                  variant="gray"
                  onClick={() => setShowConfirmation(false)}
                  disabled={assignmentRecommendationUpdateLoading}
                  className="text-sm"
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  onClick={handleConfirmSave}
                  loading={assignmentRecommendationUpdateLoading}
                  className="text-sm"
                >
                  Confirm & Save
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showHistory && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-5xl w-full max-h-[82vh] overflow-y-auto">
            <div className="p-5 border-b border-gray-200 flex items-center justify-between">
              <div>
                <h3 className="text-xl font-bold text-gray-900">
                  Settings History
                </h3>
                <p className="text-sm text-gray-500">
                  Review past configuration changes and active settings.
                </p>
              </div>
              <Button
                variant="clear"
                onClick={() => setShowHistory(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="h-6 w-6" />
              </Button>
            </div>
            <div className="p-5">
              {assignmentRecommendationHistoryLoading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
                </div>
              ) : history.length === 0 ? (
                <p className="text-gray-600 text-center py-10 text-sm">
                  No history available
                </p>
              ) : (
                <div className="space-y-5">
                  {history.map((item: any) => (
                    <div
                      key={item.id}
                      className={`rounded-xl border p-5 transition ${
                        item.is_active
                          ? "border-green-400 bg-green-50 shadow-sm"
                          : "border-gray-200 bg-white"
                      }`}
                    >
                      <div className="flex flex-col gap-4">
                        <div className="flex items-start justify-between">
                          <div className="flex items-center gap-2">
                            <span
                              className={`px-3 py-1 text-xs font-semibold rounded-full ${
                                item.is_active
                                  ? "bg-green-100 text-green-800"
                                  : "bg-gray-100 text-gray-600"
                              }`}
                            >
                              {item.is_active ? "Active" : "Previous"}
                            </span>
                            <span className="text-sm text-gray-600">
                              {new Date(item.created_at).toLocaleString()}
                            </span>
                          </div>
                          <div className="text-sm text-gray-600">
                            <span className="text-gray-500">Updated by</span>{" "}
                            {item.created_by_email || "System"}
                          </div>
                        </div>

                        {item.change_reason && (
                          <div className="text-sm text-gray-700 bg-gray-50 border border-gray-200 rounded-lg px-4 py-3">
                            <span className="font-semibold">Reason:</span>{" "}
                            {item.change_reason}
                          </div>
                        )}

                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                          {factorKeys.map(factor => (
                            <div
                              key={factor}
                              className="flex items-center justify-between bg-gray-50 border border-gray-200 rounded-lg px-3 py-2"
                            >
                              <span className="text-gray-600">
                                {factorConfig[factor].label}
                              </span>
                              <span className="font-semibold text-gray-900">
                                {item[`${factor}_weight`]}%
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AssignmentRecommendationSettings;
